import { useEffect, useState } from 'react';

/**
 * Full-screen maintenance overlay shown when the backend API is unreachable.
 * Displays an animated progress indicator so the user knows we're actively
 * retrying, and auto-redirects once the health check succeeds (handled by
 * MaintenanceContext).
 */
export function MaintenancePage() {
  const [dots, setDots] = useState('');

  // Animate the "checking" dots
  useEffect(() => {
    const id = setInterval(() => {
      setDots((prev) => (prev.length >= 3 ? '' : prev + '.'));
    }, 600);
    return () => clearInterval(id);
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-blue-50 dark:from-gray-900 dark:to-gray-800 px-4">
      <div className="max-w-lg w-full text-center">
        {/* Icon */}
        <div className="mx-auto mb-8 flex items-center justify-center w-20 h-20 rounded-full bg-blue-100 dark:bg-blue-900/40">
          <svg
            className="w-10 h-10 text-primary-700 dark:text-primary-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M11.42 15.17l-5.384-3.108A2.25 2.25 0 015.25 10.06V6.75M18.75 12a6.75 6.75 0 11-13.5 0 6.75 6.75 0 0113.5 0zM12 8.25v4.5m0 2.25h.008v.008H12v-.008z"
            />
          </svg>
        </div>

        {/* Heading */}
        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-3">
          We'll Be Right Back
        </h1>
        <p className="text-lg text-gray-600 dark:text-gray-400 mb-6">
          CloudIncidents is undergoing scheduled maintenance.
          We're working to get things back up as quickly as possible.
        </p>

        {/* Animated status */}
        <div className="inline-flex items-center gap-3 px-5 py-3 bg-white dark:bg-gray-800 rounded-full shadow-md border border-gray-200 dark:border-gray-700">
          <span className="relative flex h-3 w-3">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-3 w-3 bg-primary-600"></span>
          </span>
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Checking service status{dots}
          </span>
        </div>

        <p className="mt-8 text-sm text-gray-500 dark:text-gray-500">
          This page will automatically refresh once the service is available.
        </p>
      </div>
    </div>
  );
}
